import { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { useParams } from "react-router-dom";
import { Card } from "../../../components/Card";
import { Flex } from "../../../components/Flex";
import Loader from "../../../components/Loader";
import { Logo } from "../../../components/Logo";
import { Text } from "../../../components/Text";
import { ExternalTextLink } from "../../../components/TextLink";
import { View } from "../../../components/View";
import { useTheme } from "../../../hooks/useTheme";
import styled from "../../../styles";

export function ListingInvites() {
  const [isDesktop, setIsDesktop] = useState(false);
  let { listingId } = useParams<{ listingId: string }>();
  const theme = useTheme();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();

    const isAndroid = /(android)/g.test(ua);
    const isIOS = /(ipad|iphone|ipod)/g.test(ua);

    if (isAndroid || isIOS) {
      const customSchemeLink = listingId
        ? `com.nibble.app://listingDetails/${listingId}`
        : "com.nibble.app://";

      const storeUrl = isAndroid
        ? "https://play.google.com/store/apps/details?id=com.nibble.app"
        : "https://apps.apple.com/gb/app/nibble-app/id1637099318";

      // Android intent URL (best practice for Android)
      const androidIntentUrl = listingId
        ? `intent://listingDetails/${listingId}#Intent;scheme=com.nibble.app;package=com.nibble.app;S.browser_fallback_url=${encodeURIComponent(
            storeUrl
          )};end`
        : `intent://#Intent;scheme=com.nibble.app;package=com.nibble.app;S.browser_fallback_url=${encodeURIComponent(
            storeUrl
          )};end`;

      let fallbackTriggered = false;
      let startTime = Date.now();

      const fallback = () => {
        if (fallbackTriggered) return;
        fallbackTriggered = true;
        window.location.href = storeUrl;
      };

      const cancelFallback = () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          fallbackTriggered = true;
        }
      };

      // Different timeouts for different platforms
      const timeoutDuration = isAndroid ? 3000 : 1500;

      const handleVisibilityChange = () => {
        // Only cancel if app was likely opened (page became hidden quickly)
        const timeSinceStart = Date.now() - startTime;
        if (document.hidden && timeSinceStart < timeoutDuration) {
          cancelFallback();
        }
      };

      const handlePageHide = () => {
        const timeSinceStart = Date.now() - startTime;
        if (timeSinceStart < timeoutDuration) {
          cancelFallback();
        }
      };

      const handleFocus = () => {
        // If focus returns quickly, deep link probably failed
        const timeSinceStart = Date.now() - startTime;
        if (
          timeSinceStart > 100 &&
          timeSinceStart < 1000 &&
          !fallbackTriggered
        ) {
          fallback();
        }
      };

      // Set up event listeners
      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("pagehide", handlePageHide);
      window.addEventListener("focus", handleFocus);

      // Set fallback timeout
      timeoutRef.current = setTimeout(() => {
        if (!document.hidden) {
          fallback();
        }
      }, timeoutDuration);

      // Choose the right URL format for each platform
      const deepLinkUrl = isAndroid ? androidIntentUrl : customSchemeLink;

      // Attempt to open the app
      try {
        if (isAndroid) {
          // For Android, create a hidden iframe (sometimes more reliable)
          const iframe = document.createElement("iframe");
          iframe.style.display = "none";
          iframe.src = deepLinkUrl;
          document.body.appendChild(iframe);

          // Clean up iframe after a short delay
          setTimeout(() => {
            if (iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }
          }, 100);
        } else {
          // For iOS, direct window.location works well
          window.location.href = deepLinkUrl;
        }
      } catch (error) {
        fallback();
      }

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        window.removeEventListener("pagehide", handlePageHide);
        window.removeEventListener("focus", handleFocus);
      };
    }

    // Desktop
    setIsDesktop(true);
  }, [listingId]);

  return (
    <Wrap>
      <View margin="xl 0 0 0">
        <Logo />
      </View>
      {!isDesktop ? (
        <Content>
          <Loader />
          <Text align="center" margin="xl 0 0" size="m">
            Opening your invite in the Joli app...
          </Text>
          <Text align="center" margin="xxl 0 0" weight="semi">
            Don't have the app yet?
          </Text>
          <ExternalTextLink href="https://joliapp.com/download">
            Download it here (it's free)
          </ExternalTextLink>
        </Content>
      ) : (
        <Content>
          <Card
            shadow="heavy"
            padding="l"
            style={{ borderRadius: theme.misc.borderRadiusLarge }}
          >
            <Flex
              align="center"
              direction="column"
              margin="0 0 0"
              style={{ maxWidth: 300 }}
            >
              <QRCode
                bgColor={theme.color.card.background}
                fgColor={theme.color.typography.heading}
                size={150}
                value={window.location.href}
              />
            </Flex>
          </Card>
          <Text align="center" margin="xl 0 0" size="l">
            Scan the QR code on your phone to open the invite on Joli
          </Text>
          <Text align="center" margin="l 0 0" weight="semi">
            Don't have the app yet?
          </Text>
          <ExternalTextLink href="https://joliapp.com/download">
            Download it here (it's free!)
          </ExternalTextLink>
        </Content>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  flex: 1;
  display: flex;
  max-width: 100%;
  align-self: center;
  flex-direction: column;
  align-items: center;
  color: ${(p) => p.theme.color.typography.text};
  min-height: 100vh;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  margin: -120px ${(p) => p.theme.spacing.m} 0;
`;
